import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import interact from 'interactjs';
import { ModalableWhiteboardDrawing } from '../../../../../models/modalable-whiteboard-drawing';
import { ModalableWhiteboardVideo } from '../../../../../models/modalable-whiteboard-video';
import { ApplicationStateService } from '../../../../../services/application-state.service';
import { BookFeatureActivationService } from '../../../../../services/book-feature-activation.service';
import { ModalService } from '../../../../../services/modal.service';
import _ from 'lodash';
import { SidebarService } from '../../../../../services/sidebar.service';
import { Subscription } from 'rxjs';
import {
    WhiteboardDrawingContentComponent
} from '../../../../sidebar/whiteboard-drawing-content/whiteboard-drawing-content.component';
import { WhiteboardDrawing } from '../../../../../models/whiteboard-drawing';
import { BookviewerWhiteboardDrawingsService } from '../../../../../services/bookviewer-whiteboard-drawings.service';
import { NgClass, NgStyle } from '@angular/common';

@Component({
    selector: 'app-whiteboard-drawing',
    templateUrl: './whiteboard-drawing.component.html',
    styleUrls: ['./whiteboard-drawing.component.scss'],
    standalone: true,
    imports: [
        NgStyle,
        NgClass
    ]
})
export class WhiteboardDrawingComponent implements OnInit, OnDestroy {
    @Input() whiteboardDrawing: WhiteboardDrawing;
    modalable: ModalableWhiteboardDrawing;
    interactableWitheboard: any;
    feature = this;
    pageContainer: any;
    left: number;
    top: number;
    width = 26;
    height = 26;

    sidebarSubscription: Subscription;
    debouncedSave: (() => void) & _.Cancelable;

    constructor(
        private modalService: ModalService,
        public applicationStateService: ApplicationStateService,
        public featureActivationService: BookFeatureActivationService,
        public sidebarService: SidebarService,
        private whiteboardDrawingService: BookviewerWhiteboardDrawingsService) {
        this.debouncedSave = _.debounce(this.saveContent, 1000);
    }

    ngOnInit(): void {
        this.modalable = new ModalableWhiteboardVideo();
        Object.assign(this.modalable, this.whiteboardDrawing);

        if (this.applicationStateService.isScrollMode()) {
            this.pageContainer = $(`.bookviewer-single-scroll-page-container[data-id=${this.whiteboardDrawing.page_id}]`);
        } else {
            this.pageContainer = $(`.bookviewer-single-page-container[data-id=${this.whiteboardDrawing.page_id}]`);
        }

        this.calculateOffset();
        this.initInteract();
        if (this.sidebarService.activeComponent === WhiteboardDrawingContentComponent) {
            this.interactableWitheboard.draggable({ enabled: true });
        }
        this.sidebarSubscription = this.sidebarService.sidebarEventFired$.subscribe(event => {
            if (event.component === WhiteboardDrawingContentComponent && event.openSidebar) {
                this.interactableWitheboard.draggable({ enabled: true });
            }
            if (event.closeSidebar) {
                this.interactableWitheboard.draggable({ enabled: false });
            }
        });
    }

    ngOnDestroy(): void {
        this.sidebarSubscription.unsubscribe();
    }

    onClick(): void {
        this.modalService.openModal(this.modalable);
    }

    stopEvent($event): void {
        if ($event.stopPropagation) {
            $event.stopPropagation();
        }
    }

    preventDefault($event): void {
        $event.preventDefault('always');
    }

    /**
     * Init interactivity for the small note element.
     */
    private initInteract(): void {

        if (!this.featureActivationService.rectsActivated) {
            return;
        }

        this.interactableWitheboard = interact(`#whiteboard-rect-${this.whiteboardDrawing.id}`) as any;
        this.interactableWitheboard.feature = this;
        this.interactableWitheboard.whiteboardDrawing = this.whiteboardDrawing;
        this.interactableWitheboard.draggable({
            enabled: false,
            onmove: event => {
                this.feature.applicationStateService.preventPanning = true;
                const target = event.target;
                let translateX = event.dx;
                let translateY = event.dy;

                const container = $('.pages');
                const pageWidth = this.feature.pageContainer.innerWidth();
                const pageHeight = this.feature.pageContainer.innerHeight();

                if (container.length > 0) {
                    const rect = target.getBoundingClientRect();
                    translateX = Math.min(Math.max(container.offset().left - rect.left, event.dx),
                        container.offset().left + container.width() - rect.right);
                    translateY = Math.min(Math.max(container.offset().top - rect.top, event.dy),
                        container.offset().top + container.height() - rect.bottom);
                }

                const lOffset = this.feature.left / pageWidth * 100;
                const tOffset = this.feature.top / pageHeight * 100;

                const xProcent = this.feature.whiteboardDrawing.position.x + lOffset + ((translateX * 100) / pageWidth);
                const yProcent = this.feature.whiteboardDrawing.position.y + tOffset + ((translateY * 100) / pageHeight);

                this.feature.left = 0;
                this.feature.top = 0;

                const whiteboardSizePx = 25;
                const whiteboardSizePercent = whiteboardSizePx / pageWidth * 100;

                this.feature.whiteboardDrawing.position.x = Math.max(0, Math.min(xProcent, 100 - whiteboardSizePercent));
                this.feature.whiteboardDrawing.position.y = Math.max(0, Math.min(yProcent, 100 - whiteboardSizePercent));
            },
            onend: event => {
                this.feature.debouncedSave();
                event.target.addEventListener('click', event => event.stopImmediatePropagation(), {
                    capture: true,
                    once: true
                });
            }
        });
    }

    private saveContent(): void {
        this.feature.whiteboardDrawingService.updateWhiteboardPosition(
            this.feature.whiteboardDrawing.book_id,
            this.feature.whiteboardDrawing).subscribe();
    }

    private calculateOffset(): void {
        const defaultSize = 26;
        const minSize = 26;

        const pageContainerWidth = this.pageContainer.width();
        const pageContainerHeight = this.pageContainer.height();

        // If the page container is small the regular limitations of width, height, top and left do not apply.
        if (pageContainerWidth < defaultSize) {
            const pagePositionContainer = $('.pages-position-container');
            this.height = +pagePositionContainer.height();
            this.top = -pageContainerHeight * (this.whiteboardDrawing.position.y / 100);
            this.left = Math.min(pageContainerWidth -
                pageContainerWidth * (this.whiteboardDrawing.position.x / 100) - this.width, 0) + 10;
            this.width = pagePositionContainer.width() - 10;
        } else {
            this.width = Math.min(
                pageContainerWidth,
                Math.max(minSize, pageContainerWidth * this.whiteboardDrawing.position.w / 100));
            this.height = Math.min(
                pageContainerHeight,
                Math.max(minSize, pageContainerHeight * this.whiteboardDrawing.position.h / 100));
            this.left = Math.min(pageContainerWidth - pageContainerWidth * (this.whiteboardDrawing.position.x / 100) - this.width, 0);
            this.top = Math.min(pageContainerHeight - pageContainerHeight * (this.whiteboardDrawing.position.y / 100) - this.height, 0);
        }
    }
}
